/**
 * NSK landing page section behaviour: sticky nav scroll-spy, FAQ, document library filter, company timeline and CTA.
 * Ported from the Nachi page components in this theme with nachi renamed to nsk; each element is a progressive
 * enhancement over server-rendered markup and defines itself only once.
 */

/* ===== brand ===== */
  (function () {
    if (customElements.get('nsk-brand')) return;

    /**
     * The brand section has no behaviour of its own (DESIGN_SPEC_V2 7.3: hover motion only, nothing enters);
     * the element exists so the section follows the one-component-per-section rule and marks itself as enhanced.
     */
    class NskBrand extends HTMLElement {
      connectedCallback() {
        this.classList.add('is-enhanced');
      }

      disconnectedCallback() {
        this.classList.remove('is-enhanced');
      }
    }

    customElements.define('nsk-brand', NskBrand);
  })();

/* ===== company ===== */
  (function () {
    if (customElements.get('nsk-company')) return;

    /**
     * nsk-company: marks the root enhanced and drives motion #11 (DESIGN_SPEC_V2 5.3): one
     * IntersectionObserver adds is-in to the timeline when 30% of it is visible, then disconnects.
     * CSS draws the rail and pops the dots; without IntersectionObserver the class is added at once.
     * The observer is a plain instance property (not a #private field): Shopify's minifier targets
     * es2017 and lowers private fields with WeakMap helpers, which doubled the 9.1 byte count.
     * Everything is undone in disconnectedCallback for the theme editor's section reload.
     */
    class NskCompany extends HTMLElement {
      connectedCallback() {
        this.classList.add('is-enhanced');
        const rail = this.querySelector('.nsk-timeline');
        if (!rail) return;
        if (!('IntersectionObserver' in window)) {
          rail.classList.add('is-in');
          return;
        }
        this.io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-in');
              this.stop();
            }
          },
          { threshold: 0.3 }
        );
        this.io.observe(rail);
      }

      disconnectedCallback() {
        this.stop();
        this.classList.remove('is-enhanced');
      }

      stop() {
        if (this.io) this.io.disconnect();
        this.io = null;
      }
    }

    customElements.define('nsk-company', NskCompany);
  })();

/* ===== cta ===== */
  (function () {
    // 7.8 / 5.3 motion #10: one IntersectionObserver draws the eyebrow rule in when the block enters (threshold .2),
    // once; nothing else is scripted. Without JS, or under reduced motion, the rule is simply there (nsk-base).
    if (customElements.get('nsk-cta')) return;

    customElements.define('nsk-cta', class extends HTMLElement {
      connectedCallback() {
        var el = this.querySelector('.nsk-eyebrow');
        if (!el || !self.IntersectionObserver) return;
        var io = (this._io = new IntersectionObserver(function (es) {
          // one target: the last entry carries its latest state
          if (es[es.length - 1].isIntersecting) {
            el.classList.add('nsk-eyebrow--draw');
            io.disconnect();
          }
        }, { threshold: 0.2 }));
        io.observe(this.querySelector('.nsk-block') || this);
      }

      disconnectedCallback() {
        if (this._io) this._io.disconnect();
        this._io = null;
      }
    });
  })();

/* ===== faq ===== */
  (function () {
    if (customElements.get('nsk-faq')) return;

    /**
     * The FAQ is complete without JavaScript (native details/summary; the open rise is CSS on [open]).
     * This element adds two things: theme-editor support (selecting a question block in the editor
     * sidebar opens that item so the merchant can see the answer they are editing; deselecting closes
     * it again) and the replay of motion #22. Chromium keeps a closed answer's finished nsk-rise
     * animation alive (the closed content is display-locked, so its style is never recomputed), so on
     * every open the answer's animations are rewound to 0 through the Web Animations API: no style
     * write, no layout read. Under reduced motion nsk-base removes the animation, so nothing rewinds.
     */
    class NskFaq extends HTMLElement {
      connectedCallback() {
        this.ac = new AbortController();
        const signal = this.ac.signal;
        this.classList.add('is-enhanced');
        this.addEventListener('toggle', (event) => this.onToggle(event), { capture: true, signal });
        for (const name of ['select', 'deselect']) {
          document.addEventListener(`shopify:block:${name}`, (event) => this.onSelect(event, name === 'select'), { signal });
        }
      }

      disconnectedCallback() {
        this.ac.abort();
      }

      /**
       * toggle does not bubble, so it is captured; a toggle from anything but an opening details
       * (a closing one, or a popover) has no truthy `open` and returns.
       * @param {Event} event
       */
      onToggle(event) {
        const item = event.target;
        if (!item.open) return;
        const answer = item.querySelector('.nsk-faq__answer');
        if (answer) for (const animation of answer.getAnimations()) animation.currentTime = 0;
      }

      /**
       * Editor select opens the block's item and marks it; deselect closes only an item it opened.
       * @param {Event} event
       * @param {boolean} open
       */
      onSelect(event, open) {
        const target = event.target;
        const item = target instanceof Element && this.contains(target) ? target.closest('details') : null;
        if (!item || (open ? item.open : !item.hasAttribute('data-nsk-editor-opened'))) return;
        item.open = open;
        item.toggleAttribute('data-nsk-editor-opened', open);
      }
    }

    customElements.define('nsk-faq', NskFaq);
  })();

/* ===== library ===== */
  (function () {
    'use strict';

    if (customElements.get('nsk-library')) return;

    /**
     * Document library: category filter and search as progressive enhancements (DESIGN_SPEC_V2 7.5).
     * Cards are server-rendered links; this element only hides non-matching ones and keeps the
     * polite live region current. Strings come from the root's data attributes, never from markup.
     * One IntersectionObserver draws the block title's rule in once the header enters (motion #10).
     */
    class NskLibrary extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.timer = 0;
        this.grid = this.querySelector('[data-nsk-grid]');
        this.filter = this.querySelector('[data-nsk-filter]');
        this.search = this.querySelector('[data-nsk-search]');
        this.status = this.querySelector('[data-nsk-status]');
        this.empty = this.querySelector('[data-nsk-empty]');
        this.emptyText = this.querySelector('[data-nsk-empty-text]');
        this.clear = this.querySelector('[data-nsk-clear]');
        this.items = Array.from(this.querySelectorAll('[data-nsk-item]'));
        this.missing = Array.from(this.querySelectorAll('[data-nsk-missing]'));

        this.watch();

        if (!this.grid || !this.filter || !this.search) return;

        const { signal } = this.controller;
        this.classList.add('is-enhanced');

        this.filter.addEventListener('change', () => this.apply(), { signal });
        this.search.addEventListener(
          'input',
          () => {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.apply(), 120);
          },
          { signal }
        );
        this.search.addEventListener('search', () => this.apply(), { signal });
        if (this.clear) this.clear.addEventListener('click', () => this.reset(), { signal });

        // Theme editor: selecting a document block the filter or search has hidden shows every card again,
        // so the editor can scroll to and outline it (block events bubble up from the <li>).
        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const item = event.target instanceof Element ? event.target.closest('[data-nsk-item], [data-nsk-missing]') : null;
            if (item && item.hidden) this.reset(false);
          },
          { signal }
        );

        this.apply(false);
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
        if (this.io) this.io.disconnect();
        clearTimeout(this.timer);
        this.classList.remove('is-enhanced', 'is-in');
      }

      /** Motion #10: one-shot observer on the block header; `is-in` plays the rule draw in CSS. */
      watch() {
        const header = this.querySelector('[data-nsk-header]');
        if (!header || !('IntersectionObserver' in window)) {
          this.classList.add('is-in');
          return;
        }
        this.io = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.classList.add('is-in');
            this.io.disconnect();
          },
          { threshold: 0.2 }
        );
        this.io.observe(header);
      }

      /** @returns {Object} the checked chip as { value, label } */
      get category() {
        const checked = this.filter.querySelector('input:checked');
        if (!checked) return { value: 'all', label: this.dataset.nskAllLabel || '' };
        return { value: checked.value, label: checked.dataset.nskLabel || '' };
      }

      /**
       * Show the matching cards, hide the rest, and describe the result.
       * @param {boolean} [announce=true] write the sentence to the live region
       */
      apply(announce = true) {
        const category = this.category;
        const query = this.search.value.trim().toLowerCase();
        const filtering = category.value !== 'all' || query !== '';
        let count = 0;

        for (const item of this.items) {
          const inCategory = category.value === 'all' || item.dataset.nskCategory === category.value;
          const inQuery = query === '' || (item.dataset.nskIndex || '').includes(query);
          const show = inCategory && inQuery;
          item.hidden = !show;
          if (show) count += 1;
        }
        for (const item of this.missing) item.hidden = filtering;

        const message = count === 0 ? this.emptyMessage(category, query) : this.countMessage(count, category);
        this.grid.hidden = count === 0;
        if (this.empty) this.empty.hidden = count !== 0;
        if (this.emptyText && count === 0) this.emptyText.textContent = message;
        // 7.5 zero matches: the empty card carries the visible sentence, so the readout is visually hidden
        // (nsk-base utility) and only announces it; with matches the readout is the visible count line again.
        if (this.status) this.status.classList.toggle('nsk-visually-hidden', count === 0);
        if (announce) this.announce(message);
      }

      /**
       * @param {string} pattern a setting string with {count}, {category} or {query} tokens
       * @param {Record<string, string | number>} values
       * @returns {string}
       */
      fill(pattern, values) {
        let out = pattern;
        for (const [key, value] of Object.entries(values)) {
          out = out.split('{' + key + '}').join(String(value));
        }
        return out;
      }

      countMessage(count, category) {
        const data = this.dataset;
        if (category.value !== 'all' && data.nskPatternFiltered) {
          return this.fill(data.nskPatternFiltered, { count, category: category.label });
        }
        if (count === 1 && data.nskPatternOne) return this.fill(data.nskPatternOne, { count });
        return this.fill(data.nskPattern || '', { count });
      }

      emptyMessage(category, query) {
        const data = this.dataset;
        let pattern = query === '' ? data.nskEmptyNoQuery || '' : data.nskEmpty || '';
        if (category.value === 'all') pattern = pattern.replace(/\s+in\s+\{category\}/, '');
        return this.fill(pattern, { query, category: category.label || data.nskAllLabel || '' });
      }

      /** Clear-then-set so repeated identical sentences are still announced. */
      announce(message) {
        if (!this.status) return;
        this.status.textContent = '';
        requestAnimationFrame(() => {
          if (this.isConnected) this.status.textContent = message;
        });
      }

      /**
       * "Clear filters": back to All, empty query, focus returns to the search input (no smooth scroll: 5.4).
       * @param {boolean} [refocus=true] false when the theme editor is about to scroll to a block itself
       */
      reset(refocus = true) {
        const all = this.filter.querySelector('input[value="all"]');
        if (all) all.checked = true;
        this.search.value = '';
        this.apply();
        if (!refocus) return;
        this.search.focus({ preventScroll: true });
        this.search.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      }
    }

    customElements.define('nsk-library', NskLibrary);
  })();

/* ===== nav ===== */
  (function () {
    if (customElements.get('nsk-nav')) return;

    const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
    const OFFSET_PROPERTY = '--nsk-nav-offset';

    /**
     * Sticky in-page navigation with scroll-spy (DESIGN_SPEC_V2 6.20 / 7.2).
     * Progressive enhancement: the chips work as plain anchors without this; the ink row, the smooth scroll,
     * aria-current and the header offset are added here and removed in disconnectedCallback.
     */
    class NskNav extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.ink = this.querySelector('.nsk-nav__ink');
        this.scroller = this.querySelector('.nsk-nav__scroller');
        this.links = Array.from(this.querySelectorAll('a.nsk-nav__link'));
        this.linkByTarget = new Map();
        this.targets = [];
        this.active = [];
        this.current = null;
        this.pending = null;
        this.observer = null;
        this.endObserver = null;
        this.last = null;
        this.endVisible = false;
        this.resizeTimer = 0;
        this.pendingTimer = 0;
        this.frame = 0;
        this.inkShown = false;
        this.inkAt = 0;
        this.manualOffset = Number.parseInt(this.dataset.nskOffset || '0', 10) || 0;

        for (const link of this.links) {
          const id = this.targetId(link);
          if (id) this.linkByTarget.set(id, link);
        }

        this.classList.add('is-enhanced');
        this.applyOffset();
        this.observe();
        // the overflow flag needs the strip's layout: read it in a one-shot IntersectionObserver callback (after
        // the first layout, no forced layout inside the script task; 5.2 rule 6), which also covers a connect
        // after load, as in the theme editor
        if ('IntersectionObserver' in window) {
          const once = new IntersectionObserver(() => {
            once.disconnect();
            if (!signal.aborted) this.refresh();
          });
          once.observe(this);
        }

        this.addEventListener('click', (event) => this.onClick(event), { signal });
        // keyboard focus: Chromium leaves a partially clipped chip where it is, so Tab into a chip that straddles
        // the strip's edge would focus it under the fade; scroll the strip (never the window) to show it whole
        this.addEventListener('focusin', (event) => this.onFocusIn(event), { signal });
        window.addEventListener('resize', () => this.onResize(), { signal });
        window.addEventListener('load', () => this.refresh(), { signal, once: true });
        // the theme editor re-renders sibling sections in place, so their anchors must be observed again
        document.addEventListener('shopify:section:load', () => this.observe(), { signal });
        if (document.fonts && document.fonts.ready) {
          // the swap from the fallback family changes the chip widths, so the ink and the overflow flag are re-read
          document.fonts.ready.then(() => {
            if (!signal.aborted) this.refresh();
          });
        }
      }

      disconnectedCallback() {
        if (this.abortController) this.abortController.abort();
        if (this.observer) this.observer.disconnect();
        if (this.endObserver) this.endObserver.disconnect();
        window.clearTimeout(this.resizeTimer);
        window.clearTimeout(this.pendingTimer);
        window.cancelAnimationFrame(this.frame);
        this.observer = null;
        this.endObserver = null;
        this.last = null;
        this.endVisible = false;
        this.active = [];
        this.current = null;
        this.pending = null;
        this.classList.remove('is-enhanced', 'is-overflowing');
        document.documentElement.style.removeProperty(OFFSET_PROPERTY);
      }

      /** @returns {boolean} */
      reducedMotion() {
        return window.matchMedia(REDUCED_MOTION).matches;
      }

      /**
       * @param {HTMLAnchorElement} link
       * @returns {string} the fragment the link points at, decoded, or an empty string
       */
      targetId(link) {
        const href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#' || href.length < 2) return '';
        try {
          return decodeURIComponent(href.slice(1));
        } catch (error) {
          return href.slice(1);
        }
      }

      /**
       * 7.2 header offset: the manual range wins; at 0 the first header / banner that is sticky or fixed
       * (itself or through a sticky ancestor such as Dawn's section wrapper) supplies its bottom edge.
       * The result goes on the document root because sibling sections read it for their scroll margin.
       */
      measureOffset() {
        if (this.manualOffset > 0) return this.manualOffset;
        const headers = document.querySelectorAll('header, [role="banner"]');
        for (const header of headers) {
          if (this.contains(header)) continue;
          let node = header;
          while (node && node !== document.body) {
            const position = window.getComputedStyle(node).position;
            if (position === 'sticky' || position === 'fixed') {
              const bottom = node.getBoundingClientRect().bottom;
              return Math.max(0, Math.round(bottom));
            }
            node = node.parentElement;
          }
        }
        return 0;
      }

      applyOffset() {
        const offset = this.measureOffset();
        document.documentElement.style.setProperty(OFFSET_PROPERTY, `${offset}px`);
      }

      onResize() {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.refresh(), 150);
      }

      /** first layout, load, fonts, debounced resize: the header offset, the overflow flag and the ink; the two
          reads come before the two writes so there is one layout, not two */
      refresh() {
        const offset = this.measureOffset();
        const scroller = this.scroller;
        const overflowing = Boolean(scroller) && scroller.scrollWidth > scroller.clientWidth;
        document.documentElement.style.setProperty(OFFSET_PROPERTY, `${offset}px`);
        this.classList.toggle('is-overflowing', overflowing);
        this.placeInk(false);
      }

      /** 7.2 scroll-spy: observe every target in a band between 40% and 45% of the viewport. */
      observe() {
        if (this.observer) this.observer.disconnect();
        if (this.endObserver) this.endObserver.disconnect();
        this.targets = [];
        this.last = null;
        this.endVisible = false;
        for (const id of this.linkByTarget.keys()) {
          const target = document.getElementById(id);
          if (!target) continue;
          this.targets.push(target);
          if (!this.last || this.last.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) this.last = target;
        }
        this.active = [];
        if (!('IntersectionObserver' in window) || this.targets.length === 0) return;

        this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
          rootMargin: '-40% 0px -55% 0px',
          threshold: 0,
        });
        for (const target of this.targets) this.observer.observe(target);
        // the page end: a last section shorter than the space below the band never reaches it (tall screens, a
        // short footer), so once it is wholly on screen it counts as reached; one observer, no scroll listener
        this.endObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) this.endVisible = entry.intersectionRatio >= 0.98;
            this.syncCurrent();
          },
          { threshold: [0, 0.98, 1] }
        );
        this.endObserver.observe(this.last);
      }

      /** @param {IntersectionObserverEntry[]} entries */
      onIntersect(entries) {
        for (const entry of entries) {
          const index = this.active.indexOf(entry.target);
          if (index >= 0) this.active.splice(index, 1);
          if (entry.isIntersecting) this.active.push(entry.target);
        }
        this.syncCurrent();
      }

      /**
       * The current section from the band's contents. While a chip click's smooth scroll is in flight the spy
       * holds on the clicked target (the pill moves once, to the chosen chip, instead of hopping through every
       * section the scroll passes); the hold ends when that target enters the band, or after 1.5s. The last
       * section, wholly on screen, wins over the band (it may never reach the band: see observe).
       */
      syncCurrent() {
        const atEnd = Boolean(this.last) && this.endVisible;
        if (this.pending) {
          if (!this.active.includes(this.pending) && !(atEnd && this.pending === this.last)) return;
          // arrived: the clicked target is already current and stays so until the next band change
          this.clearPending();
          return;
        }
        if (atEnd) {
          this.setCurrent(this.last);
          return;
        }
        if (this.active.length > 0) {
          this.setCurrent(this.active[this.active.length - 1]);
          return;
        }
        // nothing in the band: above the first target there is no current section; between targets the
        // most recent one stays current
        const first = this.targets[0];
        if (first && first.getBoundingClientRect().top > window.innerHeight * 0.45) this.setCurrent(null);
      }

      /** @param {Element} target */
      holdOn(target) {
        this.pending = target;
        window.clearTimeout(this.pendingTimer);
        this.pendingTimer = window.setTimeout(() => {
          this.pending = null;
          this.syncCurrent();
        }, 1500);
      }

      clearPending() {
        window.clearTimeout(this.pendingTimer);
        this.pending = null;
      }

      /** @param {Element | null} target */
      setCurrent(target) {
        if (target === this.current) return;
        this.current = target;
        const link = target ? this.linkByTarget.get(target.id) : null;
        for (const other of this.links) {
          if (other === link) other.setAttribute('aria-current', 'location');
          else other.removeAttribute('aria-current');
        }
        this.placeInk(true);
        if (link) this.revealLink(link);
        // back above the first section: the strip returns to its start so the first chips show again
        else if (this.scroller && this.scroller.scrollLeft !== 0) {
          this.scroller.scrollTo({ left: 0, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
        }
      }

      /**
       * 6.20 ink row: one layout read of the current chip's box (inside the track, its offset parent; offsetLeft
       * is physical, as the clip-path insets are), then the two custom properties are written in a frame. With
       * no current section the pill collapses in place; a pill that was not visible jumps instead of sliding.
       * @param {boolean} animate false for layout-driven moves (resize, fonts) so the pill never slides
       */
      placeInk(animate) {
        const ink = this.ink;
        if (!ink) return;
        const link = this.current ? this.linkByTarget.get(this.current.id) : null;
        let start = this.inkAt;
        let end = this.inkAt;
        if (link) {
          start = link.offsetLeft;
          end = start + link.offsetWidth;
        }
        const jump = !animate || !this.inkShown;
        this.inkShown = Boolean(link);
        this.inkAt = start;
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => {
          if (jump) ink.classList.add('is-jump');
          ink.style.setProperty('--nsk-ink-l', `${start}px`);
          ink.style.setProperty('--nsk-ink-r', `calc(100% - ${end}px)`);
          if (jump) {
            void ink.offsetWidth;
            ink.classList.remove('is-jump');
          }
        });
      }

      /**
       * 5.3 #8 mobile strip: bring the current chip into view by scrolling the strip. The strip itself is
       * scrolled (scrollBy on the scroller, never scrollIntoView), so the window is never in the scroll chain:
       * a window move here would cancel a chip click's smooth scroll and shift the scroll-spy band.
       * @param {HTMLElement} link
       */
      revealLink(link) {
        this.scrollStripTo(link, this.reducedMotion() ? 'auto' : 'smooth');
      }

      /**
       * Nearest-edge scroll of the strip to show a chip whole, with the scroller's scroll padding (24px, the
       * fade) kept clear on the side it arrives at. Physical deltas from the two boxes, so it holds in RTL too.
       * Chips already fully inside the strip are left alone.
       * @param {HTMLElement} link
       * @param {ScrollBehavior} behavior
       */
      scrollStripTo(link, behavior) {
        const scroller = this.scroller;
        if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
        const pad = Number.parseFloat(window.getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
        const ring = 4; // outline-offset 2px + 2px ring
        const box = link.getBoundingClientRect();
        const view = scroller.getBoundingClientRect();
        let left = 0;
        if (box.left - ring < view.left) left = box.left - pad - view.left;
        else if (box.right + ring > view.right) left = box.right + pad - view.right;
        if (left !== 0) scroller.scrollBy({ left, behavior });
      }

      /**
       * 2.4.7 / 2.4.11 focus never obscured: a focused chip that straddles the strip's edge (ring included) is
       * scrolled into the strip; only the strip moves, the window stays still. Keyboard focus only: a pointer
       * press focuses the anchor on mousedown, and scrolling the strip then would move the chip out from under
       * the pointer before mouseup, so the click never fires on it; pointer focus does not match :focus-visible.
       * @param {FocusEvent} event
       */
      onFocusIn(event) {
        const link = event.target instanceof Element ? event.target.closest('.nsk-nav__link') : null;
        if (!link || !this.contains(link)) return;
        if (!link.matches(':focus-visible')) return;
        this.scrollStripTo(link, 'auto');
      }

      /**
       * 5.3 #9 chip click: smooth scroll by JS (never scroll-behavior on html), the URL keeps the fragment,
       * focus moves to the target so keyboard users continue from the section they chose.
       * @param {MouseEvent} event
       */
      onClick(event) {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target instanceof Element ? event.target.closest('a.nsk-nav__link') : null;
        if (!link || !this.contains(link)) return;
        const id = this.targetId(link);
        const target = id ? document.getElementById(id) : null;
        if (!target) return;

        event.preventDefault();
        const behavior = this.reducedMotion() ? 'auto' : 'smooth';
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', `#${encodeURIComponent(id)}`);
        }
        target.scrollIntoView({ behavior, block: 'start' });
        if (this.linkByTarget.has(id)) {
          this.holdOn(target);
          this.setCurrent(target);
        }
        if (!target.hasAttribute('tabindex')) {
          // the section is a focus destination, not a control: without this the keyboard-driven focus draws the
          // browser's ring around the whole section until the next blur
          target.setAttribute('tabindex', '-1');
          target.style.outline = 'none';
          target.addEventListener(
            'blur',
            () => {
              target.removeAttribute('tabindex');
              target.style.removeProperty('outline');
            },
            { once: true }
          );
        }
        target.focus({ preventScroll: true });
      }
    }

    customElements.define('nsk-nav', NskNav);
  })();
